import React, { useEffect, useState } from 'react';
import {
    Button,
    Image,
    Modal,
    Text,
    Loader,
    Center,
    ThemeIcon,
} from '@mantine/core';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';

import { TrainingMaterial } from '../../types/training';
import When from '../common/When';

type Props = {
    opened: boolean;
    onClose: () => void;
    material: TrainingMaterial | null;
    viewUrl: string | null;
    loading: boolean;
    onDownload?: () => void;
};

function MediaError({ onDownload }: { onDownload?: () => void }) {
    return (
        <Center className="min-h-[50vh]">
            <div className="flex max-w-md flex-col items-center rounded-xl border border-gray-200 bg-gray-50 px-8 py-10 text-center">
                <ThemeIcon
                    color="orange"
                    variant="light"
                    size={56}
                    radius="xl"
                    className="mb-4"
                >
                    <IconAlertTriangle size={28} />
                </ThemeIcon>
                <Text fw={600} size="lg" className="mb-2">
                    Could not load this preview
                </Text>
                <Text c="dimmed" size="sm" className="mb-6">
                    The file could not be shown in the browser. Download it to
                    open locally, or try again after a refresh.
                </Text>
                <When condition={Boolean(onDownload)}>
                    <Button
                        size="sm"
                        leftSection={<IconDownload size={16} />}
                        onClick={onDownload}
                    >
                        Download instead
                    </Button>
                </When>
            </div>
        </Center>
    );
}

function ViewerContent({
    fileType,
    viewUrl,
    title,
    onDownload,
}: {
    fileType: string;
    viewUrl: string;
    title: string;
    onDownload?: () => void;
}) {
    const [mediaError, setMediaError] = useState(false);

    useEffect(() => {
        setMediaError(false);
    }, [viewUrl]);

    if (mediaError) {
        return <MediaError onDownload={onDownload} />;
    }

    const handleMediaError = () => {
        setMediaError(true);
    };

    return (
        <When
            condition={fileType.startsWith('image/')}
            fallback={
                <When
                    condition={fileType.startsWith('video/')}
                    fallback={
                        <When
                            condition={fileType === 'application/pdf'}
                            fallback={
                                <Text c="dimmed">
                                    Preview is not available for this file type.
                                    Use Download instead.
                                </Text>
                            }
                        >
                            <iframe
                                src={viewUrl}
                                title={title}
                                sandbox="allow-same-origin"
                                className="w-full h-[70vh] rounded-md border-0"
                            />
                        </When>
                    }
                >
                    <video
                        src={viewUrl}
                        controls
                        preload="metadata"
                        onError={handleMediaError}
                        className="w-full max-h-[70vh] rounded-md bg-black"
                    >
                        <track kind="captions" />
                    </video>
                </When>
            }
        >
            <Image src={viewUrl} alt={title} onError={handleMediaError} />
        </When>
    );
}

export function TrainingMaterialViewer({
    opened,
    onClose,
    material,
    viewUrl,
    loading,
    onDownload,
}: Props) {
    const fileType = material?.fileType ?? '';
    const title = material?.title ?? 'Training material';

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={title}
            size="xl"
            centered
            classNames={{
                body: 'min-h-[50vh]',
            }}
        >
            <When
                condition={loading}
                fallback={
                    <When
                        condition={Boolean(viewUrl)}
                        fallback={
                            <Center className="min-h-[40vh]">
                                <Text c="dimmed">
                                    Unable to load preview. Please try again or
                                    use Download instead.
                                </Text>
                            </Center>
                        }
                    >
                        <ViewerContent
                            fileType={fileType}
                            viewUrl={viewUrl ?? ''}
                            title={title}
                            onDownload={onDownload}
                        />
                    </When>
                }
            >
                <Center className="min-h-[40vh]">
                    <Loader />
                </Center>
            </When>
        </Modal>
    );
}
